/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / erreurs axios comme U3106 */
/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id / handlers stables */
import React, { useEffect, useReducer } from 'react';
import type {
  U2000FormSBusinessHandlers,
  U2000FormStateHandlersReturn,
} from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';

import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import {
  u2000IntFormState,
  useU2000FormStateHandlers,
} from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type {
  U2000SearchPayload,
  U2000SearchResult,
} from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, {
  U2000FormStateEnumEditMode,
  type U2000FormState,
} from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { NS } from '../U2201';
import U2201JobScheduleBody from './form/U2201JobScheduleBody';
import { U2201JobScheduleFormToolBar } from './form/U2201JobScheduleFormToolBar';
import { useU2201Context } from '../U2201Context';
import {
  buildUpdateBody,
  doGetData,
  doSearch,
  doUpdateData,
  luHandleAppiError,
  normalizeJobScheduleFromApi,
  type U2201JobSchedule,
  type U2201JobScheduleFormRow,
  type U2201JobScheduleSearchRow,
} from '../U2201Service';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

export interface U2201JobScheduleFormExtendedHandlers
  extends U2000FormStateHandlersReturn<U2201JobScheduleFormRow> {
  modifyJobSchedule: () => void;
}

const U2201JobScheduleForm: React.FC = () => {
  const { id } = useParams();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2201Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(
    u2000FormState,
    u2000IntFormState
  );

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith('fr');
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U2201JobScheduleFormRow) => {
    const result: Record<string, string> = {};
    const cron = row.cronExpression?.trim() ?? '';
    if (!cron) {
      result['cronExpression'] = t('errCronRequired', {
        ns: NS,
        defaultValue: isFr ? 'L’expression cron est obligatoire.' : 'Cron expression is required.',
      });
    }
    const tz = row.timezone?.trim() ?? '';
    if (!tz) {
      result['timezone'] = t('errTimezoneRequired', {
        ns: NS,
        defaultValue: isFr ? 'Le fuseau horaire est obligatoire.' : 'Timezone is required.',
      });
    }
    const maxAttempts = Number(row.maxAttempts);
    if (!Number.isFinite(maxAttempts) || !Number.isInteger(maxAttempts) || maxAttempts < 1) {
      result['maxAttempts'] = t('errMaxAttemptsInvalid', {
        ns: NS,
        defaultValue: isFr
          ? 'Le nombre de tentatives doit être un entier ≥ 1.'
          : 'Max attempts must be an integer ≥ 1.',
      });
    }
    const rawPayload = row.payloadJson?.trim() ?? '';
    if (rawPayload !== '') {
      try {
        const parsed = JSON.parse(rawPayload);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          result['payloadJson'] = t('errPayloadInvalid', {
            ns: NS,
            defaultValue: isFr
              ? 'Le payload doit être un objet JSON.'
              : 'Payload must be a JSON object.',
          });
        }
      } catch {
        result['payloadJson'] = t('errPayloadInvalidJson', {
          ns: NS,
          defaultValue: isFr ? 'JSON invalide dans le payload.' : 'Invalid JSON in payload.',
        });
      }
    }
    return result;
  };

  const u2201BL: U2000FormSBusinessHandlers<U2201JobScheduleFormRow> = {
    newRecord: () => {
      throw new Error('U2201 V1: création non supportée');
    },

    validateForm,

    saveForme: async (row: U2201JobScheduleFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u2201FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u2201FormExtended.getOldData() as U2201JobScheduleFormRow | null;
      if (old) {
        u2201FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === '/U2201') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(
        searchStateHandlers.extractSearchParameter(),
        gridStateHandlers.extractPagination(page),
        direction
      );
    },

    onLeaveForm: () => {
      navigate('/U2201');
    },
  };

  const u2201FormHandlers = useU2000FormStateHandlers<U2201JobScheduleFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u2201BL,
  });

  const u2201FormExtended: U2201JobScheduleFormExtendedHandlers = {
    ...u2201FormHandlers,
    modifyJobSchedule: () => {
      u2201FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
  };

  const handleSearch = useU2000SearchHandlers<U2201JobScheduleSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (
      searchResult: U2000SearchResult<U2201JobScheduleSearchRow>,
      direction: U2000GridStateEnumPaginationDirection
    ) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U2201JobScheduleFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U2201JobScheduleFormRow) => {
      const normalized = normalizeJobScheduleFromApi(row as unknown as U2201JobSchedule);
      gridStateHandlers.setDataById(normalized.id, normalized as unknown as U2201JobScheduleSearchRow);
      u2201FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      setLogicalUnitBreadcrumb([
        { href: '/U2201', label: listTitle },
        { label: normalized.scheduleCode || normalized.id },
      ]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u2201FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U2201JobScheduleFormRow>({
    doSaveFn: (row: U2201JobScheduleFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U2201JobScheduleFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeJobScheduleFromApi(row as unknown as U2201JobSchedule);
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, normalized as unknown as U2201JobScheduleSearchRow);
      }
      u2201FormExtended.afterSaveData(normalized);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === '/U2201') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u2201FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u2201FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    handleGet.getData(id);
  }, [id]);

  if (u2201FormExtended.getData() === null) {
    return null;
  }

  return (
    <U2000DefaultForm
      actionsBtns={<U2201JobScheduleFormToolBar formStateHandlers={u2201FormExtended as any} />}
      searchStateHandlers={searchStateHandlers}
      gridStateHandlers={gridStateHandlers}
      formStateHandlers={u2201FormExtended as any}
    >
      <U2201JobScheduleBody formStateHandlers={u2201FormExtended as any} t={t} />
    </U2000DefaultForm>
  );
};

export default U2201JobScheduleForm;
